import {
  BottomSheet,
  Card,
  CbhIcon,
  DotSeparatedList,
  Illustration,
  Pill,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardContent, Stack } from "@mui/material";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { format, formatDistanceToNow } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";

import { type PlacementDetail } from "../api/useGetPlacement";
import { BrowseMoreJobsButton } from "./BrowseMoreJobsButton";
import { BrowseMyJobsButton } from "./BrowseMyJobs";

interface InterviewBookedDialogProps {
  modalState: UseModalState;
  placementDetails: PlacementDetail;
  selectedInterviewTime: {
    start: Date;
    end: Date;
  };
  interviewType?: "in-person-interview" | "phone-screen";
}

export function InterviewBookedDialog(props: InterviewBookedDialogProps) {
  const { modalState, placementDetails, selectedInterviewTime, interviewType } = props;
  const worker = useDefinedWorker();
  const isProspectWorkplace = Boolean(placementDetails.workplace.placementSettings?.isProspect);

  // Use the actual interview type if provided, otherwise fall back to workplace settings
  const isPhoneScreen =
    interviewType === "phone-screen" ||
    (!interviewType &&
      placementDetails.interviewSettings?.attributes.selfServeInterviewBookingType ===
        "phone-screen");

  function getTimeOfDayIcon(date: Date): "sun-filled" | "sunset-filled" {
    const hour = date.getHours();
    // Consider daytime between 6 AM and 12 PM
    return hour >= 6 && hour < 12 ? "sun-filled" : "sunset-filled";
  }

  const interviewWorkplaceDetails = [
    isDefined(placementDetails.workerTypes) && (
      <Text key="shift-types" variant="body2">
        {placementDetails.workerTypes?.join(", ")}
      </Text>
    ),
    <Text key="workplace-name" variant="body2">
      {placementDetails.workplace.name}
    </Text>,
    isDefined(placementDetails.distance) && (
      <Text key="distance" variant="body2">
        {formatDistanceInMiles(placementDetails.distance)}
      </Text>
    ),
  ].filter(Boolean);

  const handleClose = () => {
    modalState.closeModal();
  };

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={handleClose} />}
      footer={
        <DialogFooter orientation="vertical">
          <BrowseMoreJobsButton />
          <BrowseMyJobsButton
            applicationMethod={placementDetails.applicationMethod}
            buttonText="View my application"
            logMetadata={{
              workplaceId: placementDetails.workplace.id,
              placementId: placementDetails.id,
              buttonSource: "placement_details_page",
            }}
          />
        </DialogFooter>
      }
    >
      <Box sx={{ px: 5, py: 6 }}>
        <Stack spacing={7} alignItems="center" justifyContent="center">
          <Illustration type="shift-booked" />
          <Stack spacing={7} sx={{ px: 10 }}>
            <Text variant="h2" textAlign="center">
              {isPhoneScreen
                ? "Your Phone Call is booked"
                : `Your ${isProspectWorkplace ? "Walk-In" : "Interview"} is booked!`}
            </Text>
            {isPhoneScreen && (
              <Text variant="body1" textAlign="center">
                Please make sure to answer the call during the scheduled day and time!
              </Text>
            )}
          </Stack>
          <Card outlined variant="tertiary" elevation={2} sx={{ width: "100%" }}>
            <CardContent>
              <Stack spacing={6}>
                <Pill
                  size="medium"
                  sx={{ width: "fit-content" }}
                  label={
                    <DotSeparatedList>
                      {format(selectedInterviewTime.start, "EEE, MMM d")}
                      {`Starts ${formatDistanceToNow(
                        zonedTimeToUtc(selectedInterviewTime.start, worker.tmz),
                        {
                          addSuffix: true,
                        }
                      )}`}
                    </DotSeparatedList>
                  }
                />
                <Stack direction="row" spacing={4}>
                  <Stack alignItems="center" sx={{ pt: 1 }}>
                    <CbhIcon type={getTimeOfDayIcon(selectedInterviewTime.start)} />
                  </Stack>
                  <Stack spacing={2}>
                    <Text semibold variant="h4">
                      {`${format(selectedInterviewTime.start, "h:mm aaa")} - ${format(
                        selectedInterviewTime.end,
                        "h:mm aaa"
                      )}`}
                    </Text>
                    <DotSeparatedList>{interviewWorkplaceDetails}</DotSeparatedList>
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </BottomSheet>
  );
}
